/* -------------------------------------------------------------------------- */
/*                          CurrencyDropdownInput                             */
/* -------------------------------------------------------------------------- */
// Este componente utiliza DropdownInput para generar un CurrencyDropdownInput

// Fernando Hernandez 03.Jul.2026
// WD-1184: Autodetección de la moneda según el país de la zona horaria del
// navegador (fallback por IP). Solo aplica UNA vez y solo si el campo está
// vacío al cargar los catálogos, para no sobreescribir el valor en pantallas
// de edición (forceinitialvalue tiene prioridad). Desactivable con
// autoDetect={false}.

import * as currencyActions from '@redux/actions/static-catalogs/currencies';
import * as timeZoneActions from '@redux/actions/static-catalogs/time-zones';
import * as currencySelectors from '@redux/reducers/static-catalogs/currencies';
import * as timeZoneSelectors from '@redux/reducers/static-catalogs/time-zones';
import { useEffect, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { resolveDefaultCurrencyId, resolveDefaultZone } from 'utility/browserDefaults';
import DropdownInput from '../DropdownInput';

const CurrencyDropdownInput = (props) => {
  const store = useSelector((state) => state);
  const dispatch = useDispatch();
  const intl = useIntl();
  const currencies = currencySelectors.getCurrenciesList(store);
  const timeZones = timeZoneSelectors.getTimeZonesList(store);

  //Reset value with initial when values are loaded from db
  const [resetValue, setResetValue] = useState(Date.now());
  const resetDropdown = () => {
    setResetValue(Date.now());
  };

  //Fetch currencies if they have not been fetched yet
  useEffect(() => {
    if (!currencySelectors.haveCurrenciesBeenFetched(store)) {
      dispatch(currencyActions.startFetchingCurrencies());
    }
    // la zona detectada define el país de la moneda
    if (!timeZoneSelectors.haveTimezonesBeenFetched(store)) {
      dispatch(timeZoneActions.startFetchingTimeZones());
    }
  }, []);

  //Update reactselect values of  currencies when fetched is finished
  useEffect(() => {
    if (!currencySelectors.isFetchingListCurrencies(store)) {
      resetDropdown();
    }
  }, [currencySelectors.isFetchingListCurrencies(store)]);

  //Autodetección de moneda según el país de la zona del navegador cuando el campo está vacío
  const [autoDetectedCurrencyId, setAutoDetectedCurrencyId] = useState(undefined);
  const autoDetectAttempted = useRef(false);
  useEffect(() => {
    const fieldIsEmpty = props.field?.value === undefined || props.field?.value === null;
    const hasForcedValue = props.forceinitialvalue !== undefined && props.forceinitialvalue !== null;
    if (
      autoDetectAttempted.current ||
      props.autoDetect === false ||
      currencies.length === 0 ||
      timeZones.length === 0 ||
      hasForcedValue ||
      !fieldIsEmpty
    ) {
      return;
    }
    autoDetectAttempted.current = true;
    let isMounted = true;
    resolveDefaultZone(timeZones).then((zone) => {
      const currencyId = resolveDefaultCurrencyId(zone, currencies);
      const stillEmpty = props.field?.value === undefined || props.field?.value === null;
      if (isMounted && currencyId !== null && stillEmpty) {
        setAutoDetectedCurrencyId(currencyId);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [currencies.length, timeZones.length]);

  return (
    <DropdownInput
      {...props}
      key={resetValue}
      forceinitialvalue={props.forceinitialvalue ?? autoDetectedCurrencyId}
      isLoading={currencySelectors.isFetchingListCurrencies(store)}
      label={<FormattedMessage id="common.currency" />}
      options={currencies.map((curr) => {
        return {
          ...curr,
          id: curr.currencyId,
          value: curr.currencyId,
          //label: curr.currencyName,
          icon: (
            <ReactCountryFlag
              className="country-flag flag-icon"
              countryCode={curr.countryCode}
              style={{
                fontSize: '1.5em',
                lineHeight: '1.5em'
              }}
              svg
            />
          ),
          label: <FormattedMessage id={`currencies.${curr.currencyId}`} />,

          label_intl: intl.formatMessage({ id: 'currencies.' + curr.currencyId })
        };
      })}
    />
  );
};

export default injectIntl(CurrencyDropdownInput);
